// src/components/common/SupplierModal/SupplierModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  FileText,
  DollarSign,
  Check,
  Loader,
  AlertCircle
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createSupplier, updateSupplier } from '../../../services/api/supplier';
import styles from './SupplierModal.module.css';

const SupplierModal = ({ mode, supplier, initialName = '', onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    taxId: '',
    paymentTerms: '',
    creditLimit: '',
    notes: '',
    isActive: true
  });

  useEffect(() => {
    if (mode === 'edit' && supplier) {
      setFormData({
        name: supplier.name || '',
        contactPerson: supplier.contactPerson || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        city: supplier.city || '',
        country: supplier.country || '',
        taxId: supplier.taxId || '',
        paymentTerms: supplier.paymentTerms || '',
        creditLimit: supplier.creditLimit || '',
        notes: supplier.notes || '',
        isActive: supplier.isActive !== undefined ? supplier.isActive : true
      });
    }
  }, [mode, supplier]);

  useEffect(() => {
    if (mode === 'create' && initialName) {
      setFormData(prev => ({ ...prev, name: initialName }));
    }
  }, [mode, initialName]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('supplier.validation.nameRequired');
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('supplier.validation.invalidEmail');
    }

    if (formData.creditLimit && (isNaN(formData.creditLimit) || parseFloat(formData.creditLimit) < 0)) {
      newErrors.creditLimit = t('supplier.validation.invalidCreditLimit');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError(t('supplier.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const supplierData = {
        name: formData.name.trim(),
        contactPerson: formData.contactPerson.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        country: formData.country.trim() || null,
        taxId: formData.taxId.trim() || null,
        paymentTerms: formData.paymentTerms.trim() || null,
        creditLimit: formData.creditLimit ? parseFloat(formData.creditLimit) : null,
        notes: formData.notes.trim() || null
      };

      if (mode === 'edit') {
        supplierData.isActive = formData.isActive;
        const response = await updateSupplier(supplier.id, supplierData);
        success(t('supplier.supplierUpdatedSuccessfully'));
        onSuccess(response.supplier);
      } else {
        const response = await createSupplier(supplierData);
        success(t('supplier.supplierCreatedSuccessfully'));
        onSuccess(response.supplier);
      }
    } catch (err) {
      console.error('Error saving supplier:', err);
      showError(err.response?.data?.message || t(`supplier.failedTo${mode === 'edit' ? 'Update' : 'Create'}Supplier`));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h2>{mode === 'edit' ? t('supplier.editSupplier') : t('supplier.createNewSupplier')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>
            {/* Supplier Name */}
            <div className={styles.formGroup}>
              <label htmlFor="name">
                {t('supplier.supplierName')} *
              </label>
              <div className={styles.inputWrapper}>
                <User className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder={t('supplier.enterSupplierName')}
                  className={errors.name ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.name && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.name}
                </span>
              )}
            </div>

            {/* Contact Person */}
            <div className={styles.formGroup}>
              <label htmlFor="contactPerson">
                {t('supplier.contactPerson')}
              </label>
              <div className={styles.inputWrapper}>
                <User className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleFieldChange('contactPerson', e.target.value)}
                  placeholder={t('supplier.enterContactPerson')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Email */}
            <div className={styles.formGroup}>
              <label htmlFor="email">
                {t('supplier.email')}
              </label>
              <div className={styles.inputWrapper}>
                <Mail className={styles.inputIcon} size={18} />
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  placeholder={t('supplier.enterEmail')}
                  className={errors.email ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.email && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.email}
                </span>
              )}
            </div>

            {/* Phone */}
            <div className={styles.formGroup}>
              <label htmlFor="phone">
                {t('supplier.phone')}
              </label>
              <div className={styles.inputWrapper}>
                <Phone className={styles.inputIcon} size={18} />
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                  placeholder={t('supplier.enterPhone')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* City */}
            <div className={styles.formGroup}>
              <label htmlFor="city">
                {t('supplier.city')}
              </label>
              <div className={styles.inputWrapper}>
                <MapPin className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  placeholder={t('supplier.enterCity')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Country */}
            <div className={styles.formGroup}>
              <label htmlFor="country">
                {t('supplier.country')}
              </label>
              <div className={styles.inputWrapper}>
                <Globe className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="country"
                  value={formData.country}
                  onChange={(e) => handleFieldChange('country', e.target.value)}
                  placeholder={t('supplier.enterCountry')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Address */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label htmlFor="address">
                {t('supplier.address')}
              </label>
              <div className={styles.inputWrapper}>
                <MapPin className={styles.inputIcon} size={18} />
                <textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                  placeholder={t('supplier.enterAddress')}
                  rows={2}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Tax ID */}
            <div className={styles.formGroup}>
              <label htmlFor="taxId">
                {t('supplier.taxId')}
              </label>
              <div className={styles.inputWrapper}>
                <FileText className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="taxId"
                  value={formData.taxId}
                  onChange={(e) => handleFieldChange('taxId', e.target.value)}
                  placeholder={t('supplier.enterTaxId')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Payment Terms */}
            <div className={styles.formGroup}>
              <label htmlFor="paymentTerms">
                {t('supplier.paymentTerms')}
              </label>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={(e) => handleFieldChange('paymentTerms', e.target.value)}
                  placeholder={t('supplier.enterPaymentTerms')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Credit Limit */}
            <div className={styles.formGroup}>
              <label htmlFor="creditLimit">
                {t('supplier.creditLimit')}
              </label>
              <div className={styles.inputWrapper}>
                <DollarSign className={styles.inputIcon} size={18} />
                <input
                  type="number"
                  id="creditLimit"
                  value={formData.creditLimit}
                  onChange={(e) => handleFieldChange('creditLimit', e.target.value)}
                  placeholder={t('supplier.enterCreditLimit')}
                  min="0"
                  step="0.01"
                  className={errors.creditLimit ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              {errors.creditLimit && (
                <span className={styles.errorText}>
                  <AlertCircle size={12} />
                  {errors.creditLimit}
                </span>
              )}
            </div>

            {/* Active Status (only in edit mode) */}
            {mode === 'edit' && (
              <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                <div className={styles.toggleRow}>
                  <div className={styles.toggleText}>
                    <span className={styles.toggleTitle}>{t('common.active')}</span>
                    <span className={styles.toggleHint}>
                      {formData.isActive ? t('supplier.activeHint') : t('supplier.inactiveHint')}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={formData.isActive}
                    aria-label={t('common.active')}
                    onClick={() => handleFieldChange('isActive', !formData.isActive)}
                    className={`${styles.toggle} ${formData.isActive ? styles.toggleOn : ''}`}
                    disabled={isSubmitting}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label htmlFor="notes">
                {t('supplier.notes')}
              </label>
              <div className={styles.inputWrapper}>
                <FileText className={styles.inputIcon} size={18} />
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  placeholder={t('supplier.enterNotes')}
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {mode === 'edit' ? t('common.updating') : t('common.creating')}
              </>
            ) : (
              <>
                <Check size={18} />
                {mode === 'edit' ? t('supplier.updateSupplier') : t('supplier.createSupplier')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierModal;
